import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

import { colors } from 'helpers/colors'
import PaymentHelpers from 'helpers/payment_helpers'
import Button from 'components/reusable/button'
import Flex from 'components/reusable/flex'
import Indent from 'components/reusable/indent'
import Input from 'components/reusable/input'
import Text from 'components/reusable/text'
import { modalEnum } from '../modal'
import ApplicationHelper from 'helpers/application_helper'
import { useModalContext } from 'hooks/use_context'

const Wrapper = styled(Flex)`
  width: 520px;
  padding: 56px 56px 40px;
  position: relative;
  @media (max-width: 768px) {
    width: 100%;
    padding: 24px 16px 16px;
  }
`

const ButtonWrap = styled(Flex)`
  cursor: pointer;
`

const INPUT_NAME = 'Payment method'

const PaymentSelectionModal = ({ onClose, modalProps = {} }) => {
  const { canToggleApplicants, bookeeFirstName, bookeeSwipecastUserId, isSubmitDisabled, onConfirmPayment } = modalProps
  const { onOpenModal } = useModalContext()
  const [paymentsMethod, setPaymentMethod] = useState('first')
  const [closeFromApplicants, setCloseFromApplicants] = useState(true)

  const toggleCloseFromApplicants = useCallback(() => setCloseFromApplicants((prev) => !prev), [setCloseFromApplicants])

  const onChange = useCallback((val) => () => setPaymentMethod(val), [setPaymentMethod])

  const confirmCard = useCallback(
    (paymentMethodId) => {
      const payment = { stripe_source_id: paymentMethodId, method: 'card' }
      onConfirmPayment(payment, closeFromApplicants)
    },
    [onConfirmPayment, closeFromApplicants]
  )

  const confirmInvoice = useCallback(
    (email) => {
      const payment = { third_party_email: email, method: 'invoice' }
      onConfirmPayment(payment, closeFromApplicants)
    },
    [onConfirmPayment, closeFromApplicants]
  )

  const openCardSelectionModal = useCallback(
    () =>
      onOpenModal(modalEnum.cardSelection, {
        onSelectCard: (paymentMethodId) => {
          onOpenModal(modalEnum.confirmBooking, {
            bookeeFirstName,
            onConfirmBooking: () => confirmCard(paymentMethodId),
          })
        },
        bookeeSwipecastUserId,
      }),
    [onOpenModal, bookeeFirstName, confirmCard, bookeeSwipecastUserId]
  )

  const openInvoiceModal = useCallback(
    () => onOpenModal(modalEnum.invoice, { bookeeFirstName, isSubmitDisabled, onConfirmBooking: confirmInvoice }),
    [onOpenModal, bookeeFirstName, isSubmitDisabled, confirmInvoice]
  )

  const handleSubmit = useCallback(async () => {
    if (paymentsMethod && paymentsMethod !== 'first') return openInvoiceModal()
    return openCardSelectionModal()
  }, [openCardSelectionModal, openInvoiceModal, paymentsMethod])

  return (
    <Wrapper dir="column">
      <Text size={ApplicationHelper.isMobile ? 18 : 24} weight={500}>
        Payment Method
      </Text>
      <Indent bottom={40} />

      <Flex dir="column" ai="flex-start" gap={8} width="100%">
        <ButtonWrap gap={8} onClick={onChange('first')}>
          <Input name={INPUT_NAME} checked={paymentsMethod === 'first'} value="first" type="radio" width={24} />
          <Text lineHeight="170%">Credit card (recommended)</Text>
        </ButtonWrap>

        {PaymentHelpers.thirdPartyAllowed() && (
          <ButtonWrap gap={8} onClick={onChange('third')}>
            <Input name={INPUT_NAME} checked={paymentsMethod === 'third'} value="third" type="radio" width={24} />
            <Text lineHeight="170%">Invoice</Text>
          </ButtonWrap>
        )}
      </Flex>
      <Indent bottom={24} />

      {canToggleApplicants && (
        <>
          <Flex width="100%" wrap="nowrap" jc="flex-start" gap={8}>
            <Input type="checkbox" value={closeFromApplicants} onChange={toggleCloseFromApplicants} />
            <Text>Close job to new applicants</Text>
          </Flex>
          <Indent bottom={40} />
        </>
      )}

      <Button type="primary" height={40} width="100%" onClick={handleSubmit}>
        Continue
      </Button>
      <Indent bottom={16} />

      <Button height={40} width="100%" color={colors.gray[600]} borderColor="transparent" onClick={onClose}>
        Cancel
      </Button>
    </Wrapper>
  )
}

export default PaymentSelectionModal
